// Copyright 2026 Mo-Sys Engineering Ltd. All Rights Reserved.
import { createSlice } from '@reduxjs/toolkit';
import { addAsyncCases, updateStateStatus } from '../utils/utils';

import {
  fetchUser,
  fetchUserInvites,
  fetchCompanyUsers,
  fetchUsersAll,
  acceptCompanyInvitation,
  declineCompanyInvitation,
  getLocalUsers,
  createLocalUser,
  deactivateLocalUser,
  activateLocalUser,
  updateLocalUser,
  resetLocalUserPassword,
} from './userActions';

import {
  addNotificationState,
  updateNotificationState,
  deleteNotificationState,
} from './userReducers';

const initialState = {
  users: [],
  user: { invites: [] },
  notifications: [],
  status: { user: 'idle', users: 'idle' }, // possible status state : 'idle' | 'loading' | 'succeeded' | 'failed'
  error: { user: null, users: null, userMessage: null },
};

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    clearError: state => {
      state.error = initialState.error;
    },
    setUserMessageError: (state, action) => {
      // possible types: 'debug','info', 'warn', 'error', 'critical'
      const { type, message } = action.payload;
      state.error.userMessage = { type, message };
    },
    wsCreateNotification: (state, action) => {
      addNotificationState(state, action.payload);
    },
    wsUpdateNotification: (state, action) => {
      updateNotificationState(state, action.payload);
    },
    wsDeleteNotification: (state, action) => {
      deleteNotificationState(state, action.payload);
    },
  },
  extraReducers: builder => {
    addAsyncCases(builder, [
      { action: fetchCompanyUsers, key: 'users' },
      { action: fetchUsersAll, key: 'users' },
      { action: getLocalUsers, key: 'users' },
    ]);

    builder
      .addCase(fetchUser.fulfilled, (state, action) => {
        state.user = {
          ...state.user,
          ...action.payload,
        };
        updateStateStatus(state, 'user', 'succeeded');
      })
      .addCase(fetchUser.rejected, (state, action) => {
        updateStateStatus(state, 'user', 'failed', action);
      })
      .addCase(fetchUserInvites.fulfilled, (state, action) => {
        state.user = {
          ...state.user,
          invites: action.payload,
        };
      })
      .addCase(acceptCompanyInvitation.fulfilled, (state, action) => {
        const companyUuid = action.payload.company_uuid;
        // Remove the accepted invitation from the user's invites
        state.user.invites = state.user.invites.filter(
          invite => invite.company_uuid !== companyUuid
        );
        updateStateStatus(state, 'user', 'succeeded');
      })
      .addCase(acceptCompanyInvitation.rejected, (state, action) => {
        updateStateStatus(state, 'user', 'failed', action);
      })
      .addCase(declineCompanyInvitation.fulfilled, (state, action) => {
        state.user.invites = state.user.invites.filter(
          invite => invite.token !== action.payload.token
        );
        updateStateStatus(state, 'user', 'succeeded');
      })
      .addCase(declineCompanyInvitation.rejected, (state, action) => {
        updateStateStatus(state, 'user', 'failed', action);
      })
      .addCase(createLocalUser.fulfilled, (state, action) => {
        state.users.push(action.payload);
        updateStateStatus(state, 'users', 'succeeded');
      })
      .addCase(createLocalUser.rejected, (state, action) => {
        updateStateStatus(state, 'users', 'failed', action);
      })
      .addCase(deactivateLocalUser.fulfilled, (state, action) => {
        // TODO: add state management
      })
      .addCase(deactivateLocalUser.rejected, (state, action) => {
        updateStateStatus(state, 'users', 'failed', action);
      })
      .addCase(activateLocalUser.fulfilled, (state, action) => {
        // TODO: add state management
      })
      .addCase(activateLocalUser.rejected, (state, action) => {
        updateStateStatus(state, 'users', 'failed', action);
      })
      .addCase(updateLocalUser.fulfilled, (state, action) => {
        state.user = {
          ...state.user,
          ...action.payload,
        };
        updateStateStatus(state, 'user', 'succeeded');
      })
      .addCase(updateLocalUser.rejected, (state, action) => {
        updateStateStatus(state, 'user', 'failed', action);
      })
      .addCase(resetLocalUserPassword.rejected, (state, action) => {
        updateStateStatus(state, 'user', 'failed', action);
      });
  },
});

export const {
  clearError,
  setUserMessageError,
  wsCreateNotification,
  wsUpdateNotification,
  wsDeleteNotification,
} = userSlice.actions;

export const userStatus = state => state.user.status;
export const userError = state => state.user.error;

export default userSlice.reducer;
